import React,{useEffect} from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ConfigProvider } from 'antd';
import './App.css';
import { auth0Config } from './config/auth0Config';
import { VerificationStatusProvider } from './context/VerificationStatusContext';
import HomePage from './components/HomePage';
import SignupPage from './components/SignupPage';
import LoginPage from './components/LoginPage';
import SignupSuccess from './components/SignupSuccess';
import AddBusinessPage from './components/AddBusinessPage';
import Marketplace from './components/Marketplace';
import AddItemForm from './components/AddItemForm';
import Dashboard from './components/Dashboard/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import EmailVerify from './components/EmailVerify';
import ProfileForm from './components/ProfileForm';
import VerificationPending from './components/VerificationPending';
import VerificationRejected from './components/VerificationRejected';
import TagManager from "react-gtm-module";
import { useLocation } from "react-router-dom";
import mixpanel from "mixpanel-browser";

// Component to check if user is already logged in and redirect to dashboard
const AuthRedirect: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isLoading } = useAuth0();

  // While Auth0 is loading, show loading state
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  // Check if user has stored credentials (logged in before)
  if (localStorage.getItem('authToken') && localStorage.getItem('authUser')) {
    return <Navigate to="/dashboard" replace />;
  }

  // Not authenticated and no stored credentials - show signup page (children)
  return children;
};
const PageTracker: React.FC = () => {
  const location = useLocation();
  const isProd = window.location.hostname.includes("sellerportal.synergetics.ai");

  useEffect(() => {
    if(!isProd) return;
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: "page_view",
      page_path: location.pathname + location.search,
    });
  }, [location]);

  return null;
};

// App Routes component that can access Auth0 context
const AppRoutes: React.FC = () => {
  return (
    <Router>
    <PageTracker />
      <div className="App">
        <VerificationStatusProvider>
          <Routes>
            {/* Root path - Dashboard for logged in users, redirects to login if not authenticated */}
            <Route path="/" element={<ProtectedRoute element={<Dashboard />} />} />
            <Route path="/signup" element={<AuthRedirect><SignupPage /></AuthRedirect>} />
            <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
            
            {/* Public route - accessible without login */}
            <Route path="/activate_email" element={<EmailVerify />} />
            
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/pending" element={<VerificationPending />} />
            <Route path="/rejected" element={<VerificationRejected />} />
            <Route path="/add-business" element={<ProtectedRoute element={<AddBusinessPage />} />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/marketplace" element={<ProtectedRoute element={<Marketplace />} />} />
            <Route path="/profile" element={<ProtectedRoute element={<ProfileForm />} />} />
            <Route path="/welcome" element={<HomePage />} />
            <Route path="/add-item" element={<ProtectedRoute element={<AddItemForm />} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </VerificationStatusProvider>
      </div>
    </Router>
  );
};

function App() {
  const isProd = window.location.hostname.includes("sellerportal.synergetics.ai");
  const prodTrackId = '55e6dff1da531637c5e2b9459429caac'
  const stgTrackId = 'a294fad60c933706e1803928dc070b18'

  let mixPanelTrackId =  isProd ? prodTrackId : stgTrackId

  // Mixpanel
  useEffect(() => {
    mixpanel.init(mixPanelTrackId, {
      autocapture: true,
      record_sessions_percent: 100,
    })
  }, [])

  // GTM
  useEffect(() => {
    if (!isProd) return;

    TagManager.initialize({
      gtmId: "GTM-TS5XQZXN",
    });

  }, []);

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      useRefreshTokens={true}
      authorizationParams={
        {
          redirect_uri: `${window.location.origin}/login`,
          audience: auth0Config.audience || undefined,
          scope: 'openid profile email offline_access',
          connection:'email',
        }
      }
    >
      <ConfigProvider>
        <AppRoutes />
      </ConfigProvider>
    </Auth0Provider>
  );
}

export default App;
